import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@shared/components/ui/dialog';
import { sanitizeHtml } from '@shared/utils/sanitize';
import termsChartImg from '@assets/terms-and-conditions-chart.png';

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
  cancellationHours?: number;
  termsTexts: string[];
  contactEmail: string;
  phone: string;
}

export function TermsModal({
  open,
  onClose,
  cancellationHours,
  termsTexts,
  contactEmail,
  phone,
}: TermsModalProps) {
  const { t } = useTranslation('booking');

  const showCancellationChart = cancellationHours != null && cancellationHours > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl max-h-[85vh] overflow-y-auto bg-card text-foreground border-border translucent:border-surface-border">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-display">
            {t('STRING_TERMS')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('STRING_TERMS')}
          </DialogDescription>
        </DialogHeader>

        {/* Cancellation timeline chart — replicates legacy layout */}
        {showCancellationChart && (
          <div className="my-4">
            {/* Labels above the bar — positioned to align with chart markers */}
            <div className="relative flex items-end mb-1 h-6">
              {/* "24hs" centered at ~50% (the midpoint circle on the bar) */}
              <span className="absolute left-1/2 -translate-x-1/2 text-sm text-foreground/70">
                {cancellationHours}hs
              </span>
              {/* "DÍA DE LA RESERVA" aligned near the right end marker */}
              <span className="absolute right-0 text-sm font-medium text-foreground uppercase">
                {t('NEW_MODAL_TERMS_RESERVATION_DAY')}
              </span>
            </div>

            {/* Chart image */}
            <img src={termsChartImg} alt="" className="w-full" />

            {/* Labels below the bar */}
            <div className="flex justify-between mt-2 text-[11px] uppercase leading-tight">
              <div className="text-foreground/60 whitespace-pre-line">
                {t('NEW_MODAL_TERMS_FREE_CANCEL')}
              </div>
              <div className="text-red-400 text-right whitespace-pre-line">
                {t('NEW_MODAL_TERMS_CANT_CANCEL')}
              </div>
            </div>
          </div>
        )}

        {/* Custom terms paragraphs from appTexts (supports HTML) */}
        {termsTexts.length > 0 && (
          <div
            className="terms-scroll-area text-sm"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(termsTexts.join('<br/><br/>')) }}
          />
        )}

        {/* Contact info */}
        {(contactEmail || phone) && (
          <p className="text-sm text-foreground/70">
            {t('NEW_MODAL_TERMS_INQUIRES', { contactEmail, phone })}
          </p>
        )}

        {/* Accept button */}
        <button
          type="button"
          className="w-full mt-2 py-3 rounded-full bg-brand text-brand-foreground text-lg font-medium hover:bg-brand/90 transition-colors"
          onClick={onClose}
        >
          {t('STRING_ACCEPT')}
        </button>
      </DialogContent>
    </Dialog>
  );
}
